/**
 * 图片压缩函数
 * @param {File} file - 图片文件对象
 * @param {number} [maxSize=500] - 最大允许大小(KB)
 * @param {number} [initialQuality=0.9] - 初始压缩质量(0-1)
 * @param {number} [minQuality=0.5] - 最小压缩质量(0-1)
 * @param {number} [step=0.1] - 每次递归压缩质量减少的步长
 * @returns {Promise<Blob>} - 返回压缩后的图片Blob对象
 */
export async function compress(
  file,
  maxSize = 500,
  initialQuality = 0.9,
  minQuality = 0.5,
  step = 0.1
) {
  // 如果文件已经小于目标大小，直接返回
  if (file.size <= maxSize * 1024) {
    return file
  }

  // 根据原始文件大小动态调整初始质量
  let quality = initialQuality
  const sizeRatio = file.size / (maxSize * 1024)
  
  // 如果文件远大于目标大小(这里设定为2倍)，使用更低初始质量
  if (sizeRatio > 2) {
    quality = Math.max(minQuality, initialQuality - 0.3)
  } else if (sizeRatio > 1.5) {
    quality = Math.max(minQuality, initialQuality - 0.15)
  }

  // 压缩图片
  const compressedBlob = await compressToBlob(file, quality)
  
  // 如果压缩后仍然大于目标大小且质量还可以继续降低，递归压缩
  if (
    compressedBlob.size > maxSize * 1024 &&
    quality - step >= minQuality
  ) {
    return compress(
      new File([compressedBlob], file.name, { type: file.type }),
      maxSize,
      quality - step,
      minQuality,
      step
    )
  }

  return new File([compressedBlob], file.name, { type: file.type })
}

/**
 * 使用canvas压缩图片到指定质量
 * @param {File} file - 图片文件
 * @param {number} quality - 压缩质量(0-1)
 * @returns {Promise<Blob>} - 返回压缩后的Blob对象
 */
function compressToBlob(file, quality) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = function(event) {
      const img = new Image()
      img.onload = function() {
        const canvas = document.createElement("canvas")
        const ctx = canvas.getContext("2d")
        
        // 计算压缩后的宽高，保持比例
        let width = img.width
        let height = img.height
        
        // 如果图片尺寸过大，先缩小尺寸
        const maxDimension = 2000 // 最大宽度或高度
        if (width > height && width > maxDimension) {
          height *= maxDimension / width
          width = maxDimension
        } else if (height > maxDimension) {
          width *= maxDimension / height
          height = maxDimension
        }
        
        canvas.width = width
        canvas.height = height
        
        // 绘制图像到canvas
        ctx.drawImage(img, 0, 0, width, height)
        
        // 转换为Blob
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error("Canvas to Blob conversion failed"))
              return
            }
            resolve(blob)
          },
          file.type || "image/jpeg",
          quality
        )
      }
      img.onerror = reject
      img.src = event.target.result
    }
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}